import React from 'react'
import { useTheme, Typography, Button, Box, Chip, Alert } from '@mui/material'
import { Article, NewReleases } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import { FlexDiv } from '../FlexDiv'
import { AppRoutes } from '../../helpers/Routes'
import { TenantSharedDocument } from '../../types/companyDocument'

interface NewDocumentsCardProps {
    documents: TenantSharedDocument[]
    onViewDocument: (doc: TenantSharedDocument) => void
}

export const NewDocumentsCard: React.FC<NewDocumentsCardProps> = ({
    documents,
    onViewDocument,
}) => {
    const theme = useTheme()
    const navigate = useNavigate()

    if (documents.length === 0) {
        return null
    }

    const first = documents[0]
    const moreCount = documents.length - 1

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                borderRadius: 3,
                padding: 3,
                marginBottom: 3,
                border: `1px solid ${theme.palette.grey[700]}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                }}
            />

            <FlexDiv
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                }}
            >
                <FlexDiv style={{ alignItems: 'center' }}>
                    <Article
                        style={{
                            color: theme.palette.info.main,
                            marginRight: theme.spacing(1),
                            fontSize: 24,
                        }}
                    />
                    <Typography
                        variant="h6"
                        style={{ color: '#fff', fontWeight: 600 }}
                    >
                        New Documents
                    </Typography>
                </FlexDiv>
                <Chip
                    icon={<NewReleases />}
                    label={`${documents.length} new`}
                    size="small"
                    color="info"
                    style={{ fontWeight: 600 }}
                />
            </FlexDiv>

            <Alert
                severity="info"
                style={{
                    marginBottom: theme.spacing(2),
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                }}
            >
                <Typography variant="body2" style={{ color: '#fff', fontWeight: 500 }}>
                    {first.name}
                    {moreCount > 0 && ` (+${moreCount} more)`}
                </Typography>
            </Alert>

            <FlexDiv style={{ gap: theme.spacing(1) }}>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => onViewDocument(first)}
                    startIcon={<Article />}
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        flex: 1,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        },
                    }}
                >
                    View document
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate(AppRoutes.documents)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        flex: 1,
                        color: 'rgba(255,255,255,0.85)',
                        borderColor: 'rgba(255,255,255,0.2)',
                    }}
                >
                    All documents
                </Button>
            </FlexDiv>
        </Box>
    )
}
